import { Tag, TagResult, normalizeString } from "@tenforce/product-fields"

import { dataAPI } from "./api"
import { getDataApiPathPrefix } from "./api/data"

export function parseTagData(data: Api.Tag): Tag {
    return {
        id: data.Id,
        name: data.Label,
        normalized: normalizeString(data.Label),
        color: data.Color,
    }
}

export const getTagByExactName = async (
    itemId: number,
    fieldId: string,
    filter = "",
): Promise<Tag | null> => {
    return dataAPI
        .getJSON<Api.ODataGetArrayResponse<Api.Tag>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `Label eq '${filter}'`,
            },
        )
        .then((response) => {
            return response.value.map(parseTagData)[0]
        })
        .catch(() => null)
}

export const getAllTags = async (
    itemId: number,
    fieldId: string,
    filter = "",
    skip = 0,
    top = 1000,
): Promise<TagResult> => {
    return dataAPI
        .getJSON<Api.ODataGetArrayResponse<Api.Tag>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `contains(Label, '${filter}')`,
                $top: top,
                $skip: skip,
                $count: true,
            },
        )
        .then((response) => {
            const parsed = response.value.map(parseTagData)
            return {
                tags: parsed,
                currentCount: parsed.length,
                totalCount: response["@count"] ?? 0,
            }
        })
        .catch(() => ({
            tags: [],
            currentCount: 0,
            totalCount: 0,
        }))
}

export const getSelectedTags = async (
    itemId: number,
    fieldId: string,
): Promise<Tag[]> => {
    return dataAPI
        .getJSON<Api.ODataGetArrayResponse<Api.Tag>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/Value`,
        )
        .then((response) => {
            return response.value
                .map(parseTagData)
                .sort((a, b) => a.name.localeCompare(b.name))
        })
        .catch(() => [])
}

export const updateTag = async (tag: Tag): Promise<void> => {
    await dataAPI.patchJSON(`Tags/${tag.id}`, {
        Label: tag.name,
        Color: tag.color,
    })
}

export const deleteTag = async (tag: Tag): Promise<void> => {
    await dataAPI.remove(`Tags/${tag.id}`, {
        key: tag.id,
    })
}

export const createTag = async (tag: Tag): Promise<Tag> => {
    const result = await dataAPI.postJSON<
        Api.ODataGetResourceResponse<Api.Tag>
    >("Tags", {
        Label: tag.name,
        Color: tag.color,
        isActive: true,
    })

    return parseTagData(result)
}

export const sendTagPickerDeltaValue = async (
    toSelect: number[],
    toDeselect: number[],
    itemId: number,
    fieldId: string,
): Promise<void> => {
    await dataAPI.patchJSON<Api.ODataGetArrayResponse<Api.Tag>>(
        `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')`,
        {
            "@type": "#TF.TagsItemField",
            "Value@delta": [
                ...toSelect.map((id) => ({
                    "@type": "#TF.Tag",
                    "@id": `${
                        window.location.origin
                    }${getDataApiPathPrefix()}/Tags/${id}`,
                })),
                ...toDeselect.map((id) => ({
                    "@removed": {
                        reason: "changed",
                    },
                    "@type": "#TF.Tag",
                    "@id": `${
                        window.location.origin
                    }${getDataApiPathPrefix()}/Tags/${id}`,
                })),
            ],
        },
    )
}
